import { clearHouseOverrideCookie, setHouseOverrideCookie } from '@/utils/houseOverrideCookie';
import { getHouseOptions } from '@/data/house/useGetHouseById';
import { houseOverrideAtom } from '@/atoms/houseOverride/houseOverrideAtom';
import { useAppContext } from '@/providers/AppContextProvider/AppContextProvider';
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';

/**
 * Provides switchHouse and goHome actions for ATG admin company context switching.
 *
 * Both actions:
 * 1. Update the Jotai atom (reactive — triggers re-renders in all useEffectiveHouseId consumers).
 * 2. Write/clear the houseOverride cookie (persistence across page reloads).
 * 3. Remove all React Query cache entries to prevent cross-house data leaks.
 *
 * Both callbacks are memoized so callers can pass them to child props without
 * triggering unnecessary re-renders.
 */
export const useSwitchHouse = () => {
    const { deployment, token } = useAppContext();
    const setHouseOverride = useSetAtom(houseOverrideAtom);
    const queryClient = useQueryClient();

    const switchHouse = useCallback(
        (houseId: string) => {
            setHouseOverrideCookie(houseId);
            setHouseOverride(houseId);
            // Remove all cached queries so no stale data from the previous house is served.
            // Hooks using useEffectiveHouseId will refetch automatically with the new houseId.
            queryClient.removeQueries();
            // Pre-warm the cache for the new house so consumers (e.g. company profile form)
            // get a cache hit or attach to an in-flight request rather than a cold fetch.
            void queryClient.prefetchQuery(getHouseOptions({ deployment, houseId, token }));
        },
        [deployment, setHouseOverride, queryClient, token]
    );

    const goHome = useCallback(() => {
        clearHouseOverrideCookie();
        setHouseOverride(null);
        queryClient.removeQueries();
    }, [setHouseOverride, queryClient]);

    return { goHome, switchHouse };
};
